import { useMaintenanceInfo } from "@/api/services/global/maintenance";
import { QuoteScope } from "@/components/elements/QuoteScope";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";
import { text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { Box, Stack, Typography } from "@mui/material";
import { FC } from "react";

// Banner shown app-wide while maintenance mode is "readable" (users may still browse
// home/login, but should be warned). Gate window starts at 09:00 JST of the start day
// (the moment the admin-visible notice period begins) and runs through the end datetime.
export const TopAlert: FC = () => {
  const { data: rs, isLoading } = useMaintenanceInfo();
  const maintenanceInfo = rs?.data;

  const shouldShowMaintenanceMessage = (): boolean => {
    if (!maintenanceInfo?.modeStartDateTime || !maintenanceInfo?.modeEndDateTime || !maintenanceInfo?.maintenanceMessage) {
      return false;
    }

    const now = dayjs().tz("Asia/Tokyo");
    const startDateTime = dayjs(maintenanceInfo.modeStartDateTime).tz("Asia/Tokyo");
    const endDateTime = dayjs(maintenanceInfo.modeEndDateTime).tz("Asia/Tokyo");

    // Effective start time is 09:00:00 of the start date.
    const effectiveStartTime = startDateTime.startOf("day").add(9, "hour");

    return now.isAfter(effectiveStartTime) && now.isBefore(endDateTime);
  };

  // Escape HTML first so admin-authored text can never inject markup, THEN apply the
  // known-safe substitutions (time tokens + newline -> <br />).
  const escapeHtml = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

  const formatMaintenanceMessage = (message: string, startDateTime: string, endDateTime: string) => {
    return escapeHtml(message)
      .replace(/%BEGIN_TIME%/g, dayjs(startDateTime).tz("Asia/Tokyo").format(DATE_TIME_FORMAT_WITH_SECONDS))
      .replace(/%END_TIME%/g, dayjs(endDateTime).tz("Asia/Tokyo").format(DATE_TIME_FORMAT_WITH_SECONDS))
      .replace(/\n/g, "<br />");
  };

  if (isLoading || !shouldShowMaintenanceMessage() || !maintenanceInfo?.maintenanceMessage) return null;

  return (
    <Box sx={{ mb: spacing.md }}>
      <QuoteScope variant="danger">
        <Stack gap={1.25}>
          <Typography color={text.primary} variant="sub16Bold">
            irohanaメンテナンスのお知らせ
          </Typography>
          <Typography variant="body14">
            <Box
              dangerouslySetInnerHTML={{
                __html: formatMaintenanceMessage(
                  maintenanceInfo.maintenanceMessage,
                  maintenanceInfo.modeStartDateTime as string,
                  maintenanceInfo.modeEndDateTime as string,
                ),
              }}
            />
          </Typography>
        </Stack>
      </QuoteScope>
    </Box>
  );
};
